import { useEffect } from "react";
import type { FieldValues, UseFormTrigger } from "react-hook-form";
import { useFormContext } from "react-hook-form";
import { useCustomizeUIData } from "@circle-react/components/shared/CustomizeUI";
import type { RoomType } from "@circle-react/helpers/liveStreamRoomHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Typography } from "@circle-react-uikit/Typography";
import { Details } from "../../Sections/Details";
import { People } from "../../Sections/People";
import { RoomType as RoomTypeComponent } from "../../Sections/RoomType";
import { useFormSections } from "../../hooks/useFormSections";
import { useRoomType } from "../../hooks/useRoomType";

interface BodyProps {
  setRoomType: (roomType: RoomType) => void;
  triggerValidationRef: React.MutableRefObject<UseFormTrigger<FieldValues> | null>;
}

export const Body = ({ setRoomType, triggerValidationRef }: BodyProps) => {
  // Note: currentSection is already internationalized
  const { currentSection } = useCustomizeUIData();
  const roomType = useRoomType(setRoomType);
  const { sections } = useFormSections(roomType);
  const { trigger } = useFormContext();
  const shouldAllowMaxWidth = currentSection === sections.roomType;

  useEffect(() => {
    if (triggerValidationRef && trigger) {
      triggerValidationRef.current = trigger;
    }
  }, [triggerValidationRef, trigger]);

  return (
    <div
      className={classNames("mx-auto px-4 py-2 sm:px-0 sm:py-14", {
        "max-w-screen-md": !shouldAllowMaxWidth,
        "max-w-max": shouldAllowMaxWidth,
      })}
    >
      <Typography.TitleXl weight="bold hidden sm:block">
        {currentSection}
      </Typography.TitleXl>
      <div className="sm:mt-8">
        {currentSection === sections.roomType && <RoomTypeComponent />}
        {currentSection === sections.details && <Details roomType={roomType} />}
        {currentSection === sections.people && <People />}
      </div>
    </div>
  );
};
